import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import Logo from '../components/Logo';

const API = 'https://sendvigo-backend-production.up.railway.app';

const EyeIcon = ({ open }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    {open ? (
      <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></>
    ) : (
      <><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></>
    )}
  </svg>
);

const DOMINIOS_PERMITIDOS = [
  'gmail.com', 'googlemail.com', 'hotmail.com', 'hotmail.es', 'hotmail.co.uk',
  'outlook.com', 'outlook.es', 'yahoo.com', 'yahoo.es', 'icloud.com',
  'me.com', 'mac.com', 'live.com', 'live.es', 'msn.com',
  'protonmail.com', 'proton.me', 'sendvigo.app', 'sendvigo.com'
];

const isValidEmail = (e) => {
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) return false;
  const dominio = e.split('@')[1]?.toLowerCase();
  return DOMINIOS_PERMITIDOS.includes(dominio);
};

const getEmailError = (e) => {
  if (!e) return null;
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) return 'Email no válido';
  const dominio = e.split('@')[1]?.toLowerCase();
  if (!DOMINIOS_PERMITIDOS.includes(dominio)) return 'Usa un email de Gmail, Hotmail, Outlook, Yahoo o iCloud';
  return null;
};

const passwordStrength = (password) => {
  if (password.length === 0) return null;
  if (password.length < 6) return { level: 1, label: 'Muy corta', color: '#f87171' };
  if (password.length < 8) return { level: 2, label: 'Débil', color: '#fb923c' };
  if (!/[A-Z]/.test(password) || !/[0-9]/.test(password)) return { level: 3, label: 'Media', color: '#f59e0b' };
  return { level: 4, label: 'Fuerte', color: '#22c55e' };
};

const calcularEdad = (fechaNacimiento) => {
  const hoy = new Date();
  const nacimiento = new Date(fechaNacimiento);
  let edad = hoy.getFullYear() - nacimiento.getFullYear();
  const m = hoy.getMonth() - nacimiento.getMonth();
  if (m < 0 || (m === 0 && hoy.getDate() < nacimiento.getDate())) edad--;
  return edad;
};

const steps = ['cuenta', 'perfil', 'confirmar'];
const stepLabels = ['Cuenta', 'Perfil', 'Confirmar'];

export default function Register({ onBack }) {
  const { login } = useAuth();
  const [step, setStep] = useState(0);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [name, setName] = useState('');
  const [fechaNacimiento, setFechaNacimiento] = useState('');
  const [telefono, setTelefono] = useState('');
  const [aceptaTerminos, setAceptaTerminos] = useState(false);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const strength = passwordStrength(password);

  const handleNext = () => {
    setMessage('');
    if (step === 0) {
      if (!email) { setMessage('Introduce tu email'); return; }
      if (!isValidEmail(email)) { setMessage('Usa un email de Gmail, Hotmail, Outlook, Yahoo o iCloud'); return; }
      if (!password || password.length < 6) { setMessage('La contraseña debe tener al menos 6 caracteres'); return; }
    }
    if (step === 1) {
      if (!name.trim()) { setMessage('Introduce tu nombre'); return; }
      if (!fechaNacimiento) { setMessage('Introduce tu fecha de nacimiento'); return; }
      const edad = calcularEdad(fechaNacimiento);
      if (edad < 18) { setMessage('Debes ser mayor de 18 años para usar SenDViGo'); return; }
    }
    if (step < steps.length - 1) setStep(step + 1);
    else handleSubmit();
  };

  const handleSubmit = async () => {
    if (!aceptaTerminos) { setMessage('Debes aceptar los términos y condiciones'); return; }
    setLoading(true);
    try {
      const response = await fetch(`${API}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          email: email.toLowerCase().trim(),
          password,
          role: 'user',
          telefono: telefono || null,
          fechaNacimiento: fechaNacimiento || null
        })
      });
      const data = await response.json();
      if (!response.ok) { setMessage(data.error || 'Error al registrarse'); setLoading(false); return; }
      login(data.token, { id: data.id, name: data.name });
    } catch {
      setMessage('Error conectando con el servidor');
    }
    setLoading(false);
  };

  const inputStyle = {
    backgroundColor: '#13161c', border: '1px solid #2a2d35', color: 'white',
    borderRadius: '14px', padding: '14px 16px', fontSize: '14px', width: '100%', outline: 'none',
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 py-10"
      style={{ backgroundColor: '#0d0f14' }}>

      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, height: '300px',
        background: 'radial-gradient(ellipse 80% 50% at 50% -5%, #f9731612 0%, transparent 70%)',
        pointerEvents: 'none' }} />

      <div className="w-full max-w-sm relative">

        <div style={{ textAlign: 'center', marginBottom: '28px' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '10px' }}>
            <Logo />
          </div>
          <p style={{ color: '#f97316', fontSize: '12px', fontStyle: 'italic', opacity: 0.8 }}>
            Juntos llegamos más lejos 🤝
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '24px' }}>
          {steps.map((s, i) => (
            <React.Fragment key={s}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                <div style={{
                  width: '28px', height: '28px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  backgroundColor: i < step ? '#f97316' : i === step ? '#f9731620' : '#1a1d24',
                  border: `2px solid ${i <= step ? '#f97316' : '#2a2d35'}`,
                }}>
                  {i < step
                    ? <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
                    : <span style={{ color: i === step ? '#f97316' : '#4b5563', fontSize: '11px', fontWeight: '700' }}>{i + 1}</span>
                  }
                </div>
                <span style={{ color: i === step ? '#f97316' : i < step ? '#9ca3af' : '#3a3d45', fontSize: '9px', fontWeight: '600', textTransform: 'uppercase' }}>
                  {stepLabels[i]}
                </span>
              </div>
              {i < steps.length - 1 && (
                <div style={{ flex: 1, height: '2px', backgroundColor: i < step ? '#f97316' : '#2a2d35', margin: '0 4px', marginBottom: '16px' }} />
              )}
            </React.Fragment>
          ))}
        </div>

        <div style={{ backgroundColor: '#1a1d24', border: '1px solid #2a2d35', borderRadius: '24px', padding: '28px 24px' }}>

          {step === 0 && (
            <>
              <h2 style={{ color: 'white', fontWeight: '800', fontSize: '20px', marginBottom: '4px' }}>Crea tu cuenta</h2>
              <p style={{ color: '#6b7280', fontSize: '13px', marginBottom: '24px' }}>Es gratis y siempre lo será</p>

              {/* Botón Google */}
              <button onClick={() => window.location.href = 'https://sendvigo-backend-production.up.railway.app/auth/google'}
                style={{ width: '100%', backgroundColor: 'white', color: '#111318', fontWeight: '700',
                  padding: '13px', borderRadius: '14px', fontSize: '14px', border: 'none',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  gap: '10px', marginBottom: '16px' }}>
                <svg width="18" height="18" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Regístrate con Google
              </button>

              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                <div style={{ flex: 1, height: '1px', backgroundColor: '#2a2d35' }} />
                <span style={{ color: '#3a3d45', fontSize: '11px', fontWeight: '600' }}>O regístrate con email</span>
                <div style={{ flex: 1, height: '1px', backgroundColor: '#2a2d35' }} />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div>
                  <label style={{ color: '#9ca3af', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '6px' }}>Email</label>
                  <input type="email" placeholder="tu@gmail.com" value={email}
                    onChange={e => { setEmail(e.target.value); setMessage(''); }}
                    style={{ ...inputStyle, borderColor: email && getEmailError(email) ? '#f8717140' : '#2a2d35' }}
                    className="placeholder-gray-600" />
                  {email && getEmailError(email) && (
                    <p style={{ color: '#f87171', fontSize: '11px', marginTop: '4px', marginLeft: '4px' }}>{getEmailError(email)}</p>
                  )}
                </div>

                <div>
                  <label style={{ color: '#9ca3af', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '6px' }}>Contraseña</label>
                  <div style={{ position: 'relative' }}>
                    <input type={showPassword ? 'text' : 'password'} placeholder="Mínimo 6 caracteres" value={password}
                      onChange={e => { setPassword(e.target.value); setMessage(''); }}
                      style={{ ...inputStyle, paddingRight: '48px' }}
                      className="placeholder-gray-600" />
                    <button onClick={() => setShowPassword(!showPassword)}
                      style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', color: '#4b5563', background: 'none', border: 'none', cursor: 'pointer', display: 'flex' }}>
                      <EyeIcon open={showPassword} />
                    </button>
                  </div>
                  {strength && (
                    <div style={{ marginTop: '8px' }}>
                      <div style={{ display: 'flex', gap: '3px', marginBottom: '4px' }}>
                        {[1,2,3,4].map(i => (
                          <div key={i} style={{ flex: 1, height: '3px', borderRadius: '2px', backgroundColor: i <= strength.level ? strength.color : '#2a2d35' }} />
                        ))}
                      </div>
                      <p style={{ color: strength.color, fontSize: '11px', fontWeight: '600' }}>{strength.label}</p>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {step === 1 && (
            <>
              <h2 style={{ color: 'white', fontWeight: '800', fontSize: '20px', marginBottom: '4px' }}>Tu perfil</h2>
              <p style={{ color: '#6b7280', fontSize: '13px', marginBottom: '24px' }}>Cuéntanos un poco sobre ti</p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div>
                  <label style={{ color: '#9ca3af', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '6px' }}>Nombre completo</label>
                  <input placeholder="¿Cómo te llamas?" value={name}
                    onChange={e => { setName(e.target.value); setMessage(''); }}
                    style={inputStyle} className="placeholder-gray-600" />
                </div>

                <div>
                  <label style={{ color: '#9ca3af', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '6px' }}>
                    Fecha de nacimiento
                  </label>
                  <input type="date" value={fechaNacimiento}
                    onChange={e => { setFechaNacimiento(e.target.value); setMessage(''); }}
                    max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
                    style={{ ...inputStyle, colorScheme: 'dark' }} />
                  {fechaNacimiento && calcularEdad(fechaNacimiento) < 18 && (
                    <p style={{ color: '#f87171', fontSize: '11px', marginTop: '4px', marginLeft: '4px' }}>
                      Debes ser mayor de 18 años para usar SenDViGo
                    </p>
                  )}
                  {fechaNacimiento && calcularEdad(fechaNacimiento) >= 18 && (
                    <p style={{ color: '#22c55e', fontSize: '11px', marginTop: '4px', marginLeft: '4px' }}>
                      ✓ Mayor de edad confirmado
                    </p>
                  )}
                </div>

                <div>
                  <label style={{ color: '#9ca3af', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '6px' }}>
                    Teléfono <span style={{ color: '#3a3d45', fontWeight: '400', textTransform: 'none' }}>(opcional)</span>
                  </label>
                  <input type="tel" placeholder="+34 600 000 000" value={telefono}
                    onChange={e => { setTelefono(e.target.value); setMessage(''); }}
                    style={inputStyle} className="placeholder-gray-600" />
                </div>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <h2 style={{ color: 'white', fontWeight: '800', fontSize: '20px', marginBottom: '4px' }}>Casi listo 🙌</h2>
              <p style={{ color: '#6b7280', fontSize: '13px', marginBottom: '24px' }}>Revisa tus datos antes de unirte</p>

              <div style={{ backgroundColor: '#111318', border: '1px solid #2a2d35', borderRadius: '14px', padding: '16px', marginBottom: '20px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: '#6b7280', fontSize: '12px' }}>Email</span>
                    <span style={{ color: 'white', fontSize: '13px', fontWeight: '600' }}>{email}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: '#6b7280', fontSize: '12px' }}>Nombre</span>
                    <span style={{ color: 'white', fontSize: '13px', fontWeight: '600' }}>{name}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: '#6b7280', fontSize: '12px' }}>Edad</span>
                    <span style={{ color: '#22c55e', fontSize: '13px', fontWeight: '600' }}>{calcularEdad(fechaNacimiento)} años ✓</span>
                  </div>
                  {telefono && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: '#6b7280', fontSize: '12px' }}>Teléfono</span>
                      <span style={{ color: 'white', fontSize: '13px', fontWeight: '600' }}>{telefono}</span>
                    </div>
                  )}
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <p style={{ color: '#6b7280', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '10px' }}>
                  Tu nivel de verificación
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '20px', height: '20px', borderRadius: '50%', backgroundColor: '#22c55e20', border: '1px solid #22c55e40', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <span style={{ color: '#22c55e', fontSize: '10px' }}>✓</span>
                    </div>
                    <span style={{ color: '#9ca3af', fontSize: '12px' }}>Email registrado</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '20px', height: '20px', borderRadius: '50%', backgroundColor: '#22c55e20', border: '1px solid #22c55e40', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <span style={{ color: '#22c55e', fontSize: '10px' }}>✓</span>
                    </div>
                    <span style={{ color: '#9ca3af', fontSize: '12px' }}>Mayor de 18 años confirmado</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '20px', height: '20px', borderRadius: '50%', backgroundColor: '#f9731620', border: '1px solid #f9731640', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <span style={{ color: '#f97316', fontSize: '10px' }}>○</span>
                    </div>
                    <span style={{ color: '#3a3d45', fontSize: '12px' }}>Teléfono verificado — próximamente</span>
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '18px', cursor: 'pointer' }}
                onClick={() => setAceptaTerminos(!aceptaTerminos)}>
                <div style={{
                  width: '20px', height: '20px', borderRadius: '6px', flexShrink: 0, marginTop: '1px',
                  backgroundColor: aceptaTerminos ? '#f97316' : '#111318',
                  border: `1.5px solid ${aceptaTerminos ? '#f97316' : '#3a3d45'}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {aceptaTerminos && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>}
                </div>
                <p style={{ color: '#9ca3af', fontSize: '12px', lineHeight: '1.5' }}>
                  Acepto los <span style={{ color: '#f97316', fontWeight: '600' }}>términos y condiciones</span> y la <span style={{ color: '#f97316', fontWeight: '600' }}>política de privacidad</span> de SenDViGo
                </p>
              </div>
            </>
          )}

          {message && (
            <div style={{ backgroundColor: '#ff000015', border: '1px solid #ff000030', color: '#f87171', borderRadius: '10px', padding: '10px 14px', fontSize: '13px', marginTop: '14px', marginBottom: '4px' }}>
              {message}
            </div>
          )}

          <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
            {step > 0 && (
              <button onClick={() => { setStep(step - 1); setMessage(''); }}
                style={{ backgroundColor: '#111318', border: '1px solid #2a2d35', color: '#9ca3af', fontWeight: '600', padding: '14px 20px', borderRadius: '14px', fontSize: '14px', cursor: 'pointer' }}>
                ← Atrás
              </button>
            )}
            <button onClick={handleNext} disabled={loading}
              style={{ flex: 1, backgroundColor: '#f97316', color: 'white', fontWeight: '700', padding: '14px', borderRadius: '14px', fontSize: '14px', border: 'none', cursor: loading ? 'default' : 'pointer', opacity: loading ? 0.8 : 1 }}>
              {loading ? 'Creando cuenta...' : step === steps.length - 1 ? '🚀 Unirme a la comunidad' : 'Continuar →'}
            </button>
          </div>
        </div>

        <div style={{ textAlign: 'center', marginTop: '20px' }}>
          <p style={{ color: '#6b7280', fontSize: '13px' }}>
            ¿Ya tienes cuenta?{' '}
            <button onClick={onBack} style={{ color: '#f97316', fontWeight: '700', background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px' }}>
              Iniciar sesión
            </button>
          </p>
        </div>

        <p style={{ color: '#2a2d35', fontSize: '11px', textAlign: 'center', marginTop: '24px', lineHeight: '1.6' }}>
          Al unirte aceptas compartir el camino con respeto y buena voluntad 🤝
        </p>
      </div>
    </div>
  );
}